"use client";

import * as React from "react";
import Link from "next/link";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { cn } from "@modernatc/ui/cn";

const STORAGE_KEY = "modernatc:sidebar-collapsed";

interface DesktopSidebarProps {
  /** Rendered sidebar body (server-component children). Labels inside
   *  should use the `sidebar-label` class so they hide when collapsed. */
  children: React.ReactNode;
}

/**
 * Collapsible desktop sidebar shell.
 *
 * Starts at the `lg` breakpoint so portrait iPads retain the full content
 * width and use the drawer navigation instead. Landscape iPads and larger
 * displays get the collapsible sidebar.
 *
 * - Persists collapsed/expanded state in localStorage so reloads
 *   don't snap users back to expanded.
 * - Sets a `data-collapsed` attribute on the <aside> so the
 *   SidebarBody can hide its label spans via the `group/sidebar`
 *   data-variant CSS in globals.css.
 * - Toggle button uses standard PanelLeftClose / PanelLeftOpen
 *   icons (Notion/Linear convention).
 */
export function DesktopSidebar({ children }: DesktopSidebarProps) {
  const [collapsed, setCollapsed] = React.useState(false);

  React.useEffect(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved === "true") setCollapsed(true);
    } catch {
      // localStorage unavailable (private mode, embedded contexts) —
      // default to expanded silently.
    }
  }, []);

  const toggle = React.useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  return (
    <aside
      data-collapsed={collapsed}
      className={cn(
        "group/sidebar hidden shrink-0 flex-col border-r border-[var(--color-panel-edge)] bg-[var(--color-panel)] transition-[width] duration-150 lg:flex",
        collapsed ? "w-16" : "w-64",
      )}
    >
      <div
        className={cn(
          "flex h-14 items-center border-b border-[var(--color-panel-edge)]",
          collapsed ? "justify-center" : "justify-between px-4",
        )}
      >
        {!collapsed ? (
          <Link
            href="/dashboard"
            className="text-callsign text-sm font-semibold text-[var(--color-ink)]"
          >
            MODERN<span className="text-[var(--color-amber)]">ATC</span>
          </Link>
        ) : null}
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="rounded-[var(--radius-sm)] p-2 text-[var(--color-ink-muted)] transition hover:bg-[var(--color-panel-raised)] hover:text-[var(--color-ink)]"
        >
          {collapsed ? (
            <PanelLeftOpen className="size-4" aria-hidden />
          ) : (
            <PanelLeftClose className="size-4" aria-hidden />
          )}
        </button>
      </div>
      {children}
    </aside>
  );
}
